import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Clock, 
  Target, 
  Shield, 
  Search,
  Palette,
  Layout,
  Info,
  Settings as SettingsIcon
} from 'lucide-react';
import { SettingsModal } from '../settings/shared/SettingsModal';
import { TabNavigation } from '../settings/shared/TabNavigation';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { SEOSection } from '../settings/shared/SEOSection';
import { firestoreService } from '../../services/firestoreService';

export const LeitnerSettings = ({ book, bookId, isOpen, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [uploadStates, setUploadStates] = useState({});
  
  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [headerBackgroundFile, setHeaderBackgroundFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [ogImageFile, setOgImageFile] = useState(null);
  
  const [logoPreview, setLogoPreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState('');
  const [ogImagePreview, setOgImagePreview] = useState('');

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: Info },
    { id: 'design', label: 'Design & Styling', icon: Palette },
    { id: 'header', label: 'Header Settings', icon: Layout },
    { id: 'leitner', label: 'Leitner System', icon: BookOpen },
    { id: 'tests', label: 'Test Settings', icon: Target },
    { id: 'privacy', label: 'Privacy & Access', icon: Shield },
    { id: 'seo', label: 'SEO & Metadata', icon: Search }
  ];

  useEffect(() => {
    initializeFormData();
  }, [book]);

  const initializeFormData = () => {
    setFormData({
      botName: book?.botName || book?.title || '',
      description: book?.description || '',
      botCategory: book?.botCategory || 'leitner',
      logoUrl: book?.logoUrl || '',
      coverImageUrl: book?.coverImageUrl || '',
      
      published: book?.published !== false,
      
      accessLevel: book?.accessLevel || 'public',
      requireLogin: book?.requireLogin || false,
      allowIndexing: book?.allowIndexing !== false,
      authorizedUsers: book?.authorizedUsers || [],
      allowPrint: book?.allowPrint !== false,
      allowDownload: book?.allowDownload || false,
      enableRightClickProtection: book?.enableRightClickProtection || false,
      passwordProtected: book?.passwordProtected || false,
      accessPassword: book?.accessPassword || '',
      
      primaryColor: book?.styling?.primaryColor || '#6366f1',
      backgroundColor: book?.styling?.backgroundColor || '#0f172a',
      headerColor: book?.styling?.headerColor || '#1e293b',
      headerTextColor: book?.styling?.headerTextColor || '#f1f5f9',
      headerOpacity: book?.styling?.headerOpacity || '100',
      textColor: book?.styling?.textColor || '#f1f5f9',
      accentColor: book?.styling?.accentColor || '#8b5cf6',
      fontFamily: book?.styling?.fontFamily || 'Inter, sans-serif',
      headerRadius: book?.styling?.headerRadius || '0px',
      logoInvert: book.styling?.logoInvert || 0,
      cardRadius: book?.styling?.cardRadius || '12px',
      shadowLevel: book?.styling?.shadowLevel || 'medium',
      
      headerSettings: {
        showLogo: book?.leitnerSettings?.showLogo ?? book?.headerSettings?.showLogo ?? true,
        showTitle: book?.leitnerSettings?.showTitle ?? book?.headerSettings?.showTitle ?? true,
        showDescription: book?.leitnerSettings?.showDescription ?? book?.headerSettings?.showDescription ?? false,
        headerHeight: book?.leitnerSettings?.headerHeight ?? book?.headerSettings?.headerHeight ?? 80,
        logoHeight: book?.leitnerSettings?.logoHeight ?? book?.headerSettings?.logoHeight ?? 40,
        position: book?.headerSettings?.position || 'sticky',
        layout: book?.headerSettings?.layout || 'default',
        ...book?.headerSettings
      },
      
      leitnerSettings: {
        timerEnabled: book?.leitnerSettings?.timerEnabled ?? true,
        defaultTimerMode: book?.leitnerSettings?.defaultTimerMode || 'perQuestion',
        defaultTimePerQuestion: book?.leitnerSettings?.defaultTimePerQuestion || 60,
        defaultTimeForSession: book?.leitnerSettings?.defaultTimeForSession || 300,
        enableTourOnFirstVisit: book?.leitnerSettings?.enableTourOnFirstVisit ?? true,
        showProgressBar: book?.leitnerSettings?.showProgressBar ?? true,
        showBoxCounts: book?.leitnerSettings?.showBoxCounts ?? true,
        enableSoundEffects: book?.leitnerSettings?.enableSoundEffects ?? false,
        allowSharing: book?.leitnerSettings?.allowSharing ?? true,
        enableAnalytics: book?.leitnerSettings?.enableAnalytics ?? true,
        customCSS: book?.leitnerSettings?.customCSS || ''
      },
      
      testSettings: {
        allowTestCreation: book?.leitnerSettings?.allowTestCreation ?? true,
        enableTestHistory: book?.leitnerSettings?.enableTestHistory ?? true,
        showPerformanceStats: book?.leitnerSettings?.showPerformanceStats ?? true,
        maxQuestionsPerTest: book?.leitnerSettings?.maxQuestionsPerTest || 50
      },
      
      seoTitle: book?.seoTitle || book?.botName || '',
      seoDescription: book?.seoDescription || book?.description || '',
      seoKeywords: book?.seoKeywords || '',
      ogImage: book?.ogImage || '',
      customDomain: book?.customDomain || '',
      customPath: book?.customPath || '',
      favicon: book?.favicon || ''
    });

    setLogoPreview(book?.logoUrl || '');
    setCoverImagePreview(book?.coverImageUrl || '');
    setHeaderBackgroundPreview(book?.headerBackgroundUrl || '');
    setFaviconPreview(book?.favicon || '');
    setOgImagePreview(book?.ogImage || '');
  };

  const handleInputChange = (path, value) => {
    setFormData(prev => {
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        let current = newData;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          current = current[key];
        }
        
        current[keys[keys.length - 1]] = value;
        return newData;
      }
      
      return { ...prev, [path]: value };
    });
    setHasChanges(true);
  };

  const handleFileUpload = async (file, fileType) => {
    if (!file || !book?.id) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [fileType]: 'File size must be less than 5MB' }));
      return;
    }

    setUploadStates(prev => ({ ...prev, [fileType]: 'uploading' }));
    setErrors(prev => ({ ...prev, [fileType]: null }));

    try {
      const path = `books/${book.id}/${fileType}`;
      const result = await firestoreService.uploadFile(file, path, book.id);
      
      // Ensure we only store the URL string, not an object
      const urlString = typeof result === 'string' ? result : result.url;
      
      // Update formData with URL string
      const fieldName = fileType === 'logo' ? 'logoUrl' : 
                        fileType === 'coverImage' ? 'coverImageUrl' : 
                        fileType;
      handleInputChange(fieldName, urlString);
      
      // Update preview state
      if (fileType === 'logo') setLogoPreview(urlString);
      else if (fileType === 'coverImage') setCoverImagePreview(urlString);
      else if (fileType === 'headerBackground') setHeaderBackgroundPreview(urlString);
      else if (fileType === 'favicon') setFaviconPreview(urlString);
      else if (fileType === 'ogImage') setOgImagePreview(urlString);
      
      setUploadStates(prev => ({ ...prev, [fileType]: 'success' }));
      setHasChanges(true);
    } catch (error) {
      console.error(`Error uploading ${fileType}:`, error);
      setErrors(prev => ({ ...prev, [fileType]: `Failed to upload ${fileType}` }));
      setUploadStates(prev => ({ ...prev, [fileType]: 'error' }));
    }
  };

  const handleFaviconSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setFaviconPreview(reader.result);
      reader.readAsDataURL(file);
      await handleFileUpload(file, 'favicon');
    }
  };

  const handleOgImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setOgImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setOgImagePreview(reader.result);
      reader.readAsDataURL(file);
      await handleFileUpload(file, 'ogImage');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setErrors({});

    try {
      const uploadPromises = [];
      const uploadedUrls = {};

      if (faviconFile) {
        uploadPromises.push(
          firestoreService.uploadFile(faviconFile, `books/${book.id}/favicon`, book.id)
            .then(url => { uploadedUrls.favicon = url; })
        );
      }

      if (ogImageFile) {
        uploadPromises.push(
          firestoreService.uploadFile(ogImageFile, `books/${book.id}/og-image`, book.id)
            .then(url => { uploadedUrls.ogImage = url; })
        );
      }

      await Promise.all(uploadPromises);

      const updatedBook = {
        ...book,
        botName: formData.botName,
        title: formData.botName,
        description: formData.description,
        botCategory: formData.botCategory,
        logoUrl: formData.logoUrl,
        coverImageUrl: formData.coverImageUrl,
        published: formData.published,
        accessLevel: formData.accessLevel,
        requireLogin: formData.requireLogin,
        allowIndexing: formData.allowIndexing,
        authorizedUsers: formData.authorizedUsers,
        allowPrint: formData.allowPrint,
        allowDownload: formData.allowDownload,
        enableRightClickProtection: formData.enableRightClickProtection,
        passwordProtected: formData.passwordProtected,
        accessPassword: formData.accessPassword,
        
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          headerOpacity: formData.headerOpacity,
          textColor: formData.textColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          headerRadius: formData.headerRadius,
          logoInvert: formData.logoInvert,
          cardRadius: formData.cardRadius,
          shadowLevel: formData.shadowLevel
        },
        
        headerSettings: formData.headerSettings,
        
        leitnerSettings: {
          ...formData.leitnerSettings,
          ...formData.testSettings,
          showLogo: formData.headerSettings.showLogo,
          showTitle: formData.headerSettings.showTitle,
          showDescription: formData.headerSettings.showDescription,
          headerHeight: formData.headerSettings.headerHeight,
          logoHeight: formData.headerSettings.logoHeight
        },
        
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        ogImage: typeof formData.ogImage === 'string' ? formData.ogImage : formData.ogImage?.url || '',
        favicon: typeof formData.favicon === 'string' ? formData.favicon : formData.favicon?.url || '',
        
        updatedAt: new Date()
      };

      await firestoreService.updateBook(book.id, updatedBook);
      onUpdate(updatedBook);
      setHasChanges(false);
      onClose();
    } catch (error) {
      console.error('Error saving leitner settings:', error);
      setErrors({ general: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <BasicInfoTab
            formData={formData}
            onInputChange={handleInputChange}
            onFileUpload={handleFileUpload}
            errors={errors}
            bookId={book?.id}
            templateType="leitner"
          />
        );

      case 'design':
        return (
          <DesignBrandingSection
            formData={formData}
            onInputChange={handleInputChange}
            bookId={book?.id}
          />
        );

      case 'header':
        return (
          <HeaderSettings
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={handleFileUpload}
          />
        );

      case 'leitner':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Leitner System Configuration</h3>
              
              <div className="space-y-4">
                {[
                  { key: 'leitnerSettings.timerEnabled', label: 'Enable Timer', desc: 'Allow timed study sessions' },
                  { key: 'leitnerSettings.enableTourOnFirstVisit', label: 'Enable Onboarding Tour', desc: 'Show tour for first-time users' },
                  { key: 'leitnerSettings.showProgressBar', label: 'Show Progress Bar', desc: 'Display progress during sessions' },
                  { key: 'leitnerSettings.showBoxCounts', label: 'Show Box Counts', desc: 'Display question counts in each box' },
                  { key: 'leitnerSettings.enableSoundEffects', label: 'Enable Sound Effects', desc: 'Play sounds for correct/incorrect answers' },
                  { key: 'leitnerSettings.allowSharing', label: 'Allow Sharing', desc: 'Enable share buttons' },
                  { key: 'leitnerSettings.enableAnalytics', label: 'Enable Analytics', desc: 'Track performance and progress' }
                ].map(({ key, label, desc }) => (
                  <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={key.includes('.') ? formData.leitnerSettings?.[key.split('.')[1]] : formData[key]}
                      onChange={(e) => handleInputChange(key, e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-sm text-gray-500">{desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timer Settings</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Time per Question (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.leitnerSettings?.defaultTimePerQuestion}
                    onChange={(e) => handleInputChange('leitnerSettings.defaultTimePerQuestion', parseInt(e.target.value))}
                    min="5"
                    max="300"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Session Time (seconds)
                  </label>
                  <input
                    type="number"
                    value={formData.leitnerSettings?.defaultTimeForSession}
                    onChange={(e) => handleInputChange('leitnerSettings.defaultTimeForSession', parseInt(e.target.value))}
                    min="60"
                    max="3600"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <BookOpen className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 mb-2">How the Leitner System Works</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    Questions start in Box 1. When answered correctly, they move to the next box. 
                    Incorrect answers send them back to Box 1.
                  </p>
                  <div className="grid grid-cols-5 gap-2">
                    {['Box 1', 'Box 2', 'Box 3', 'Box 4', 'Box 5'].map((box, idx) => (
                      <div key={box} className="text-center">
                        <div 
                          className="h-12 rounded-lg flex items-center justify-center text-white font-semibold text-xs mb-1"
                          style={{ backgroundColor: `rgb(${99 + idx * 20}, ${102 + idx * 20}, ${241 - idx * 20})` }}
                        >
                          {box}
                        </div>
                        <p className="text-xs text-gray-600">
                          {idx === 0 ? 'Daily' : idx === 1 ? '2 days' : idx === 2 ? 'Weekly' : idx === 3 ? 'Bi-weekly' : 'Monthly'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'tests':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Center Configuration</h3>
              
              <div className="space-y-4">
                {[
                  { key: 'testSettings.allowTestCreation', label: 'Allow Test Creation', desc: 'Users can create custom tests' },
                  { key: 'testSettings.enableTestHistory', label: 'Enable Test History', desc: 'Track and display past test results' },
                  { key: 'testSettings.showPerformanceStats', label: 'Show Performance Stats', desc: 'Display detailed performance analytics' }
                ].map(({ key, label, desc }) => (
                  <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.testSettings?.[key.split('.')[1]]}
                      onChange={(e) => handleInputChange(key, e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-sm text-gray-500">{desc}</div>
                    </div>
                  </label>
                ))}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Questions per Test
                  </label>
                  <input
                    type="number"
                    value={formData.testSettings?.maxQuestionsPerTest}
                    onChange={(e) => handleInputChange('testSettings.maxQuestionsPerTest', parseInt(e.target.value))}
                    min="5"
                    max="200"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <PrivacySection
            bookId={bookId}
            formData={formData}
            onInputChange={handleInputChange}
            templateType="leitner"
          />
        );

      case 'seo':
        return (
          <SEOSection
            formData={formData}
            onInputChange={handleInputChange}
            onImageUpload={(file, fileType) => {
              if (fileType === 'favicon') {
                const event = { target: { files: [file] } };
                handleFaviconSelect(event);
              } else if (fileType === 'ogImage') {
                const event = { target: { files: [file] } };
                handleOgImageSelect(event);
              }
            }}
            uploadStates={uploadStates}
            errors={errors}
            bookId={book?.id}
            faviconPreview={faviconPreview}
            ogImagePreview={ogImagePreview}
            onRemoveFavicon={() => {
              setFaviconFile(null);
              setFaviconPreview('');
              handleInputChange('favicon', '');
            }}
            onRemoveOgImage={() => {
              setOgImageFile(null);
              setOgImagePreview('');
              handleInputChange('ogImage', '');
            }}
          />
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

    return (
    <SettingsModal
        isOpen={isOpen}
        onClose={onClose}
        title="Leitner System Settings"
        subtitle="Configure your spaced repetition learning system"
        onSave={handleSave}
        saving={saving}
        hasChanges={hasChanges}
    >
        <div className="flex h-full overflow-hidden">
        <TabNavigation
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={setActiveTab}
        />
        </div>
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            {renderTabContent()}
          </div>
        </div>
    </SettingsModal>
    );
};